import { useState, useEffect } from "react";
import { Box, Typography, Switch, Chip, CircularProgress } from "@mui/material";

const WaitlistHeader = ({
  isWaitlistEnabled = false,
  onToggle,
  loading = false,
  disabled = false,
  totalWaiting,
}) => {
  const [enabled, setEnabled] = useState(isWaitlistEnabled);

  useEffect(() => {
    setEnabled(isWaitlistEnabled);
  }, [isWaitlistEnabled]);

  const handleToggle = async (e) => {
    const nextVal = e.target.checked;
    if (nextVal === enabled) return;
    if (onToggle) {
      const result = await onToggle(!nextVal);
      if (result?.success) {
        setEnabled(nextVal);
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", md: "center" },
        gap: "var(--space-md)",
        padding: "var(--space-lg)",
        backgroundColor: "var(--white)",
        borderRadius: "var(--radius-lg)",
        border: "1px solid var(--border-color-light)",
        boxShadow: "var(--shadow-sm)",
        marginBottom: "var(--space-lg)",
      }}
    >
      {/* Title & Informative Description */}
      <Box sx={{ maxWidth: "640px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-sm)",
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: "var(--text-xl)",
              fontWeight: 700,
              color: "var(--text-color-primary)",
            }}
          >
            Waitlist Management
          </Typography>
          {totalWaiting !== undefined && (
            <Chip
              label={`${totalWaiting} Waiting`}
              size="small"
              sx={{
                fontSize: "var(--text-xs)",
                height: "22px",
                fontWeight: 600,
                backgroundColor: "var(--theme-gray-bg)",
                border: "1px solid var(--border-color-light)",
                color: "var(--gray-darkest)",
              }}
            />
          )}
        </Box>

        <Typography
          sx={{
            fontSize: "var(--text-sm)",
            color: "var(--text-color-gray)",
            marginTop: "6px",
            lineHeight: 1.5,
          }}
        >
          Control platform access for new users. Toggle this setting to turn the
          waitlist on or off.
        </Typography>

        <Typography
          sx={{
            fontSize: "var(--text-xs)",
            fontWeight: 500,
            marginTop: "6px",
            color: enabled ? "var(--btn-purple-color)" : "var(--gray-light)",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: enabled
                ? "var(--btn-purple-color)"
                : "var(--gray-lightest)",
              display: "inline-block",
            }}
          />
          {enabled
            ? "Waitlist is active — New signups are queued on the waitlist."
            : "Waitlist is inactive — New users can register and access directly."}
        </Typography>
      </Box>

      {/* Toggle Control Area */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-md)",
          backgroundColor: "var(--theme-gray-bg)",
          padding: "8px 16px",
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border-color-light)",
          alignSelf: { xs: "stretch", sm: "auto" },
          justifyContent: { xs: "space-between", sm: "flex-end" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {loading && (
            <CircularProgress
              size={16}
              sx={{ color: "var(--btn-purple-color)" }}
            />
          )}
          <Chip
            size="small"
            label={enabled ? "Waitlist ON" : "Waitlist OFF"}
            sx={{
              fontSize: "var(--text-xs)",
              fontWeight: 600,
              backgroundColor: enabled
                ? "rgba(48, 26, 255, 0.08)"
                : "transparent",
              color: enabled ? "var(--btn-purple-color)" : "var(--gray-light)",
              border: `1px solid ${
                enabled
                  ? "rgba(48, 26, 255, 0.25)"
                  : "var(--border-color-light)"
              }`,
            }}
          />
        </Box>

        <Switch
          checked={enabled}
          onChange={handleToggle}
          disabled={disabled || loading}
          slotProps={{ input: { "aria-label": "Toggle Waitlist Mode" } }}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "var(--btn-purple-color)",
              "&:hover": {
                backgroundColor: "rgba(48, 26, 255, 0.08)",
              },
            },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "var(--btn-purple-color)",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default WaitlistHeader;
